import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DAY_FULL, TIMEZONES, DEFAULT_PAY, payOf, periodStartISO } from "@/lib/payPeriod";

// Editable copy of a company's pay + business settings (shared by the setup wizard and Settings).
export const paySettingsFrom = (company) => {
  const pay = payOf(company);
  return {
    pay_period: pay.period, pay_week_start: pay.start_day,
    pay_anchor: pay.anchor || periodStartISO(new Date(), { ...pay, period: "weekly" }),
    timezone: pay.timezone || DEFAULT_PAY.timezone,
    payroll_email: company?.payroll_email || "", client_reports: !!company?.client_reports,
    abn: company?.abn || "", address: company?.address || "",
    phone: company?.phone || "", contact_email: company?.contact_email || "", overtime_rule: company?.overtime_rule || "shift8",
  };
};

export const paySettingsPayload = (v) => ({
  pay_period: v.pay_period, timezone: v.timezone, payroll_email: v.payroll_email.trim(),
  client_reports: v.client_reports, abn: v.abn.trim(), address: v.address.trim(),
  phone: v.phone.trim(), contact_email: v.contact_email.trim(), overtime_rule: v.overtime_rule,
  ...(v.pay_period === "fortnightly" ? { pay_anchor: v.pay_anchor } : { pay_week_start: Number(v.pay_week_start) }),
});

const Seg = ({ value, onChange, options, testid }) => (
  <div className="grid grid-cols-2 gap-1.5" data-testid={testid}>
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={`h-9 rounded-md border text-sm font-medium transition-colors ${value === v ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"}`}>{label}</button>
    ))}
  </div>
);

export const CompanyPaySection = ({ value: v, onChange }) => {
  const set = (k) => (x) => onChange({ ...v, [k]: x?.target ? x.target.value : x });
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border p-4 space-y-3" data-testid="pay-settings">
        <p className="text-sm font-medium">Pay period</p>
        <Seg value={v.pay_period} onChange={set("pay_period")} options={[["weekly", "Weekly"], ["fortnightly", "Fortnightly"]]} testid="pay-period" />
        {v.pay_period === "weekly" ? (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Pay week starts on</Label>
            <Select value={String(v.pay_week_start)} onValueChange={(x) => onChange({ ...v, pay_week_start: Number(x) })}>
              <SelectTrigger data-testid="pay-week-start"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4, 5, 6, 0].map((d) => <SelectItem key={d} value={String(d)}>{DAY_FULL[d]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">A pay fortnight starts on</Label>
            <Input type="date" value={v.pay_anchor} onChange={set("pay_anchor")} data-testid="pay-anchor" />
            {v.pay_anchor && <p className="text-xs text-muted-foreground">Every second {DAY_FULL[new Date(`${v.pay_anchor}T00:00:00`).getDay()]} from this date.</p>}
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">State (time zone for timesheets and pay periods)</Label>
          <Select value={v.timezone} onValueChange={set("timezone")}>
            <SelectTrigger data-testid="pay-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>{TIMEZONES.map(([tz, label]) => <SelectItem key={tz} value={tz}>{label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Payroll email (your bookkeeper)</Label>
          <Input type="email" value={v.payroll_email} onChange={set("payroll_email")} placeholder="bookkeeper@company.com.au" data-testid="pay-payroll-email" />
          <p className="text-xs text-muted-foreground">Payroll PDFs go here. A depot can use its own from Timesheets → Email payroll.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Overtime is counted as</Label>
          <Seg value={v.overtime_rule} onChange={set("overtime_rule")} options={[["shift8", "Over 8 h in a shift"], ["week38", "Over 38 h in a week"]]} testid="pay-overtime" />
        </div>
      </div>
      <div className="rounded-lg border border-border p-4 space-y-3" data-testid="business-settings">
        <p className="text-sm font-medium">Business details <span className="text-muted-foreground font-normal">(shown at the top of your PDFs)</span></p>
        <div className="grid sm:grid-cols-[1fr_2fr] gap-3">
          <Input value={v.abn} onChange={set("abn")} maxLength={20} placeholder="ABN" data-testid="business-abn" />
          <Input value={v.address} onChange={set("address")} maxLength={200} placeholder="Business address" data-testid="business-address" />
          <Input value={v.phone} onChange={set("phone")} maxLength={20} placeholder="Phone" data-testid="business-phone" />
          <Input type="email" value={v.contact_email} onChange={set("contact_email")} maxLength={200} placeholder="Business email" data-testid="business-email" />
        </div>
        <p className="text-xs text-muted-foreground">Depots that trade under other details can set their own in the Depots tab.</p>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm">Email clients a job report</p>
            <p className="text-xs text-muted-foreground">When a job is completed, the client gets a PDF with photos and times.</p>
          </div>
          <Switch checked={v.client_reports} onCheckedChange={set("client_reports")} data-testid="business-client-reports" />
        </div>
      </div>
    </div>
  );
};
